/**
 * Run document work with Word's change tracking switched off, then put the
 * user's setting back.
 *
 * Template steps, table revisions and page inserts must not land as the
 * user's own tracked changes (a header replaced under tracking showed up as
 * "Elly Sinden Deleted ¶" and nothing in the pane could accept it). Word
 * cannot set a revision's author, so anything that should carry no revision
 * or a mytender.io revision is written with tracking off.
 *
 * The mode is restored in a fresh `Word.run`: a batch that threw cannot be
 * reused, and a `finally` on the failed context would throw again and leave
 * tracking off for good. Hosts below WordApi 1.4 cannot read or set the mode;
 * there the work simply runs.
 */
import { documentCapabilities } from "./snapshot";

type TrackingMode = Word.ChangeTrackingMode | `${Word.ChangeTrackingMode}`;

/** Switch tracking off (when the host allows) and return the mode it was in. */
const switchOff = async (): Promise<TrackingMode | null> =>
  Word.run(async (context) => {
    const document = context.document;
    document.load("changeTrackingMode");
    await context.sync();
    const previous = document.changeTrackingMode;
    if (previous !== Word.ChangeTrackingMode.off) {
      document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();
    }
    return previous;
  });

const restore = async (previous: TrackingMode | null): Promise<void> => {
  if (previous === null || previous === Word.ChangeTrackingMode.off) return;
  await Word.run(async (context) => {
    context.document.changeTrackingMode = previous as Word.ChangeTrackingMode;
    await context.sync();
  });
};

/**
 * Run `work` with change tracking off. `work` may call `Word.run` as many
 * times as it likes (a failed insert can be retried in a fresh batch).
 * @param work - The document work.
 * @returns Whatever `work` returns.
 */
export const withTrackingOff = async <T>(work: () => Promise<T>): Promise<T> => {
  const canTrack = documentCapabilities().tracked_changes;
  let previous: TrackingMode | null = null;
  if (canTrack) {
    try {
      previous = await switchOff();
    } catch (error) {
      // The mode could not be read: run anyway rather than refuse the work.
      console.warn("Could not switch change tracking off", error);
      previous = null;
    }
  }
  try {
    return await work();
  } finally {
    if (canTrack) await restore(previous).catch((error) => console.warn("Could not restore change tracking", error));
  }
};
